import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Kpi } from "@/components/Kpi";
import StatusBadge from "@/components/StatusBadge";
import ShipmentMap from "@/components/ShipmentMap";
import { Package, TruckTrailer, WarningCircle, Leaf, ArrowRight, ClockCountdown } from "@phosphor-icons/react";

export default function ControlTower() {
  const [stats, setStats] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then((r) => setStats(r.data)).catch(() => {});
    api.get("/shipments").then((r) => setShipments(r.data)).catch(() => {});
    api.get("/alerts").then((r) => setAlerts(r.data)).catch(() => {});
  }, []);

  const active = shipments.filter((s) => ["in_transit", "dispatched"].includes(s.status));

  return (
    <Layout title="Control Tower" subtitle="Live shipment operations & sustainability overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi testid="kpi-active" label="Active Shipments" value={stats?.active ?? "—"} icon={TruckTrailer} accent="#2563EB" sub={`${stats?.total ?? 0} total`} />
        <Kpi testid="kpi-transit" label="In Transit" value={stats?.by_status?.in_transit ?? "—"} icon={Package} accent="#2563EB" />
        <Kpi testid="kpi-delays" label="Delay Flags" value={stats?.delays ?? "—"} icon={WarningCircle} accent="#DC2626" sub="ETA > 12h" />
        <Kpi testid="kpi-carbon" label="Total CO₂" value={stats ? (stats.total_co2_kg / 1000).toFixed(2) : "—"} unit="t" icon={Leaf} accent="#059669" sub={`${stats?.total_distance_km ?? 0} km moved`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* map */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-heading font-semibold text-slate-900">Live Map</h3>
            <span className="text-xs text-slate-400">{active.length} vehicles active · last-known positions</span>
          </div>
          <div className="h-[440px]">
            <ShipmentMap shipments={shipments} />
          </div>
        </div>

        {/* exception queue */}
        <div className="bg-white border border-slate-200 rounded-md flex flex-col">
          <div className="px-5 py-3 border-b border-slate-200">
            <h3 className="font-heading font-semibold text-slate-900">Exception Queue</h3>
          </div>
          <div className="flex-1 overflow-y-auto ct-scroll max-h-[440px]">
            {alerts.length === 0 && <div className="p-5 text-sm text-slate-400">No alerts.</div>}
            {alerts.map((a) => (
              <div key={a.id} data-testid={`alert-row-${a.id}`} className="px-5 py-3 border-b border-slate-100 flex gap-3">
                <div className={`mt-1 h-2 w-2 rounded-full shrink-0 ${a.severity === "warning" ? "bg-red-500" : a.severity === "success" ? "bg-green-500" : "bg-blue-500"}`} />
                <div>
                  <div className="text-sm text-slate-700 leading-snug">{a.message}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">{new Date(a.created_at).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* active shipments table */}
      <div className="mt-6 bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-heading font-semibold text-slate-900">Shipments</h3>
          <button data-testid="view-all-shipments" onClick={() => navigate("/shipments")} className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 font-medium">
            View all <ArrowRight size={14} />
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="px-5 py-2.5 font-bold">Code</th>
                <th className="px-5 py-2.5 font-bold">Route</th>
                <th className="px-5 py-2.5 font-bold">Driver</th>
                <th className="px-5 py-2.5 font-bold">ETA</th>
                <th className="px-5 py-2.5 font-bold">CO₂</th>
                <th className="px-5 py-2.5 font-bold">Status</th>
              </tr>
            </thead>
            <tbody>
              {shipments.slice(0, 8).map((s) => (
                <tr key={s.id} data-testid={`ct-shipment-${s.code}`} onClick={() => navigate(`/shipments/${s.id}`)}
                  className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer">
                  <td className="px-5 py-3 font-mono font-medium text-slate-900">{s.code}</td>
                  <td className="px-5 py-3 text-slate-600">{s.origin.name} → {s.destination.name}</td>
                  <td className="px-5 py-3 text-slate-600">{s.driver_name}</td>
                  <td className="px-5 py-3 text-slate-600">
                    <span className="inline-flex items-center gap-1"><ClockCountdown size={14} className="text-slate-400" />{s.eta_hours}h</span>
                  </td>
                  <td className="px-5 py-3 font-mono text-slate-600">{s.co2_kg} kg</td>
                  <td className="px-5 py-3"><StatusBadge status={s.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
